'use client';

// Apple Pay / Google Pay express button for /comix/unlock and /comix/checkout.
//
// Self-contained on purpose (revert = delete this folder + the one-line insert in each
// page). Renders NOTHING unless the server-side `wallet_checkout` flag is on AND this
// device has a usable wallet; Apple devices get Apple Pay, others Google Pay.
//
// Tapping the button opens a sheet with OUR contact form (email; plus delivery address
// for printed books only) and a server quote. This is not wallet address autofill —
// browser autofill may help, the wallet sheet does not supply the address in phase 1.
// The second tap opens the wallet sheet for a J5 hold; the server verifies, captures and
// creates the order. A hold is never shown as "paid".

import { Dialog } from '@headlessui/react';
import { motion } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  chargeWallet,
  detectWallet,
  type HostedFieldsParams,
  type WalletMethod,
  type WalletMode,
  type WalletOutcome,
} from '@/lib/wallet/client/hosted-fields';
import { formatUsdCents } from '@/lib/wallet/money';
import { markCheckoutStarted } from '@/lib/comix/use-pending-approvals';
import { trackMetaEvent } from '@/lib/meta-pixel';
import { trackEvent } from '@/lib/amplitude';
import {
  US_STATES,
  validateContact,
  type ContactErrors,
  type ShippingAddressInput,
  LULU_ADDRESS_LIMITS,
  formatUsPhone,
} from '@/lib/wallet/validate-contact';

type Product = 'digital' | 'printed';
type Phase =
  | 'quoted' | 'authorizing' | 'held' | 'capturing' | 'paid_order_pending'
  | 'paid' | 'releasing' | 'released' | 'not_charged' | 'support';

interface AttemptItem {
  variantId: string;
  quantity: number;
  unitCents: number;
  product: string;
}

interface AttemptView {
  attemptId: string;
  phase: Phase;
  locked: boolean;
  product: Product | 'bag';
  variantId?: string;
  /** The comics paid for (bag: several) — decides where to land after payment. */
  runIds?: string[];
  /** Line items (bag: every cart line) — for Meta contents. */
  items?: AttemptItem[];
  quantity?: number;
  /** Price before the 2nd-copy discount (2+ copies of one book). */
  listSubtotalCents?: number;
  subtotalCents: number;
  shippingCents: number;
  taxCents: number;
  totalCents: number;
  quoteExpiresAt: string;
  orderName: string | null;
  /** Server decides: true only for an internal wallet order Shopify can't report. */
  sitePurchaseEvent?: boolean;
  declineReason?: 'card_not_supported' | 'declined' | null;
}

/**
 * Meta pixel for the wallet flow (Shopify's Meta app never sees these orders, so nothing
 * else reports them). Once per attempt per event; Purchase is also guarded across reloads.
 */
function walletMetaEvent(
  event: 'AddToCart' | 'InitiateCheckout' | 'AddPaymentInfo' | 'Purchase',
  a: { attemptId: string; product: Product | 'bag'; variantId?: string; subtotalCents: number; totalCents: number; items?: AttemptItem[] },
) {
  const key = `pwm_wallet_meta_${event}_${a.attemptId}`;
  try {
    if (sessionStorage.getItem(key) || localStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
    if (event === 'Purchase') localStorage.setItem(key, '1');
  } catch {
    /* storage blocked — still fire once in this render path */
  }
  const value = (event === 'AddToCart' ? a.subtotalCents : a.totalCents) / 100;
  const items = a.items?.length ? a.items : a.variantId ? [{ variantId: a.variantId, quantity: 1, unitCents: a.subtotalCents, product: a.product }] : [];
  const ids = items.length ? Array.from(new Set(items.map((i) => i.variantId))) : undefined;
  trackMetaEvent(
    event,
    {
      value,
      currency: 'USD',
      content_ids: ids,
      content_name:
        a.product === 'bag' ? 'Custom Comic Books (Bag)' : a.product === 'printed' ? 'Custom Comic Book (Printed)' : 'Custom Comic Book (Digital)',
      num_items: items.reduce((n, i) => n + i.quantity, 0) || 1,
      contents: items.length ? items.map((i) => ({ id: i.variantId, quantity: i.quantity, item_price: i.unitCents / 100 })) : undefined,
    },
    // Purchase uses the wallet order id (also the Order row's id) so a future server-side
    // Conversions API event can be deduplicated against it.
    event === 'Purchase' ? `wallet-${a.attemptId}` : `wallet-${event}-${a.attemptId}`,
  );
}

const IN_FLIGHT: Phase[] = ['authorizing', 'held', 'capturing', 'paid_order_pending'];

const EMPTY_ADDR: ShippingAddressInput = {
  firstName: '', lastName: '', address1: '', address2: '', city: '', province: '', zip: '', countryCode: 'US', phone: '',
};

/** Admin login token (set by /comix/admin) — proves a tester for wallet TEST mode. */
function adminHeader(): Record<string, string> {
  try {
    const t = localStorage.getItem('admin_token');
    return t ? { 'x-admin-token': t } : {};
  } catch {
    return {};
  }
}

/** Longest the page's buy buttons wait (loading CTA) for the wallet check before showing Checkout. */
const RESOLVE_MAX_MS = 4000;

async function postJson(url: string, body: unknown, keepalive = false) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(url.endsWith('/authorize-result') ? adminHeader() : {}) },
    body: JSON.stringify(body),
    keepalive,
  });
  const json = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, json };
}

export default function WalletExpressPay({
  runId,
  sessionId,
  product,
  productLabel,
  quantity = 1,
  buttonClassName = 'mt-3',
  onLockedChange,
  onVisibleChange,
  onResolvingChange,
  bag,
}: {
  /** Single book: its run. Bag mode: unused (the cart's lines carry their runs). */
  runId: string | null | undefined;
  sessionId: string | null | undefined;
  product: Product;
  productLabel: string;
  /** Copies of the book (single-book mode). The 2nd and up are priced by the server. */
  quantity?: number;
  /** Classes for the wallet button itself (spacing is the page's call). */
  buttonClassName?: string;
  /** true while a wallet payment for this book is in flight / done — disable other checkouts. */
  onLockedChange?: (locked: boolean) => void;
  /** true when the wallet button is actually shown (flag on, tester, phone with a wallet). */
  onVisibleChange?: (visible: boolean) => void;
  /** true while it is still unknown whether the wallet button will show (render a loading CTA). */
  onResolvingChange?: (resolving: boolean) => void;
  /**
   * Bag mode: pay for the whole bag. `signature` changes whenever lines / quantities /
   * totals change (forces a fresh quote); `product` should be 'printed' if any line is.
   */
  bag?: { signature: string; hasPrinted: boolean };
}) {
  const [mode, setMode] = useState<WalletMode | null>(null);
  const [method, setMethod] = useState<WalletMethod | null>(null);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [addr, setAddr] = useState<ShippingAddressInput>(EMPTY_ADDR);
  // Lulu's verdict on the address (printed): warnings + its suggested correction, and whether
  // the customer chose "use my address anyway". Any edit to the address clears both.
  const [addrCheck, setAddrCheck] = useState<{ warnings: string[]; suggested: Partial<ShippingAddressInput> | null } | null>(null);
  const [addrConfirmed, setAddrConfirmed] = useState(false);
  // Street-address type-ahead (Google Places via /api/comix/wallet/address; on only when the
  // server has a key). `addrQuery` changes only when the customer TYPES in the street field.
  const [addressAutocomplete, setAddressAutocomplete] = useState(false);
  const [addrQuery, setAddrQuery] = useState('');
  const [addrSuggestions, setAddrSuggestions] = useState<{ placeId: string; text: string }[]>([]);
  const placesTokenRef = useRef<string | null>(null);
  // The product on screen RIGHT NOW. A quote request is async: one started for Digital (the
  // background one-tap prep) can finish after the customer switched to Printed — it must be
  // dropped, never shown or paid under the other product (seen: "Printed" sheet charging $25).
  // Close the suggestion list on a tap OUTSIDE it / the street field — never on blur: on iPhone
  // (WebKit, incl. Chrome iOS) the field can blur for a moment when the list renders and the
  // sheet scrolls, which made the list flash and vanish before it could be tapped.
  useEffect(() => {
    if (!addrSuggestions.length) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Element | null;
      if (t?.closest?.('[data-addr-suggest]') || (t as HTMLInputElement)?.name === 'address1') return;
      setAddrSuggestions([]);
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [addrSuggestions.length]);
  // ZIP → City + State (free Zippopotam lookup, no key). Fills only what the customer hasn't
  // typed: State is set when empty, City only when empty — never overwrites their input.
  const zipLookedUpRef = useRef('');
  useEffect(() => {
    const zip = (addr.zip || '').slice(0, 5);
    if (!/^\d{5}$/.test(zip) || zipLookedUpRef.current === zip) return;
    zipLookedUpRef.current = zip;
    let cancelled = false;
    fetch(`https://api.zippopotam.us/us/${zip}`, { signal: AbortSignal.timeout(4000) })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        const place = j?.places?.[0];
        if (cancelled || !place) return;
        setAddr((a) => {
          if ((a.zip || '').slice(0, 5) !== zip) return a; // ZIP changed meanwhile
          const state = String(place['state abbreviation'] || '');
          const city = String(place['place name'] || '');
          const next = {
            ...a,
            province: a.province || state,
            city: a.city || (city.length <= LULU_ADDRESS_LIMITS.city ? city : ''),
          };
          return next.province === a.province && next.city === a.city ? a : next;
        });
      })
      .catch(() => { /* lookup is a convenience only */ });
    return () => { cancelled = true; };
  }, [addr.zip]);

  // Remember the last delivery address on THIS device (localStorage, per-viewer convenience)
  // and offer it pre-filled next time. Saved only after Lulu accepted it (successful quote).
  const SAVED_ADDR_KEY = 'pwm_wallet_address_v1';
  useEffect(() => {
    if (product !== 'printed') return;
    try {
      const saved = JSON.parse(localStorage.getItem(SAVED_ADDR_KEY) || 'null');
      if (saved && typeof saved === 'object' && saved.countryCode === 'US') {
        setAddr((a) => (a.address1 || a.zip || a.firstName ? a : { ...a, ...saved }));
      }
    } catch {
      /* storage unavailable */
    }
  }, [product]); // eslint-disable-line react-hooks/exhaustive-deps

  const productRef = useRef(product);
  productRef.current = product;
  const quantityRef = useRef(quantity);
  quantityRef.current = quantity;
  const wantKind = bag ? 'bag' : product; // what attempt.product must be for this screen
  useEffect(() => {
    if (!addressAutocomplete || addrQuery.trim().length < 4) {
      setAddrSuggestions([]);
      return;
    }
    placesTokenRef.current ??= typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : String(Date.now());
    const t = setTimeout(async () => {
      const r = await postJson('/api/comix/wallet/address', { sessionId, sessionToken: placesTokenRef.current, input: addrQuery }).catch(() => null);
      const list = r?.ok && Array.isArray(r.json?.suggestions) ? r.json.suggestions : [];
      setAddrSuggestions(list);
      // Phones: with the keyboard up, a list opening BELOW a mid-sheet field lands behind the
      // keyboard (seen on iPhone — requests answered, nothing visible). Lift the street field
      // to the top of the sheet so the suggestions sit above the keyboard.
      if (list.length) {
        document.querySelector<HTMLInputElement>('input[name="address1"]')?.scrollIntoView({ block: 'start' });
      }
    }, 250);
    return () => clearTimeout(t);
  }, [addrQuery, addressAutocomplete, sessionId]);
  const editAddr = (next: ShippingAddressInput) => {
    setAddr(next);
    setAddrCheck(null);
    setAddrConfirmed(false);
  };
  const [errors, setErrors] = useState<ContactErrors>({});
  const panelRef = useRef<HTMLDivElement | null>(null);
  // Smooth step changes (address page ↔ order sheet ↔ status): the panel animates between a
  // measured height (sheet) and the viewport height (full page) instead of swapping instantly.
  const innerRef = useRef<HTMLDivElement | null>(null);
  const [contentH, setContentH] = useState(0);
  const [viewH, setViewH] = useState(() => (typeof window === 'undefined' ? 0 : window.innerHeight));
  // md breakpoint (768px): desktop shows the address step as a box instead of a full page.
  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const sync = () => setIsDesktop(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);
  useEffect(() => {
    if (!open) return;
    const sync = () => setViewH(window.innerHeight);
    sync();
    window.addEventListener('resize', sync);
    return () => window.removeEventListener('resize', sync);
  }, [open]);
  useEffect(() => {
    const el = innerRef.current;
    if (!open || !el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setContentH(el.offsetHeight));
    ro.observe(el);
    setContentH(el.offsetHeight);
    return () => ro.disconnect();
  });
  // The full-height page's header only gets its divider once the form has scrolled under it.
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => { if (!open) setScrolled(false); }, [open]);
  // After a failed Next: bring the first field with a problem (or the address warning) into view.
  useEffect(() => {
    if (!open) return;
    if (Object.keys(errors).length === 0 && !addrCheck) return;
    const id = requestAnimationFrame(() => {
      const el =
        panelRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]') ??
        panelRef.current?.querySelector<HTMLElement>('[role="alert"]');
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (el.tagName !== 'DIV') el.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(id);
  }, [errors, addrCheck, open]);
  const [attempt, setAttempt] = useState<AttemptView | null>(null);
  const [hf, setHf] = useState<HostedFieldsParams | null>(null);
  const [confirmDest, setConfirmDest] = useState(false);
  const [busy, setBusy] = useState(false);
  /** Something prevents express pay for this order (rate limit, not available, can't start): hide the button, Checkout takes over. */
  const [unavailable, setUnavailable] = useState(false);
  /** Email the app already has for this visitor — we never ask for it again. */
  const [knownEmail, setKnownEmail] = useState<string | null>(null);
  /** The wallet script never called back; the server is checking with Tranzila. */
  const [checking, setChecking] = useState(false);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const ready = !!((bag || runId) && sessionId);
  /** The flag + device check has finished (whatever the answer) — until then the page shows a loading CTA. */
  const [detectDone, setDetectDone] = useState(false);

  // 1. Flag + device detection. No Tranzila script loads unless the flag is on.
  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    (async () => {
      try {
        const cfg = await fetch(`/api/comix/wallet/config?sessionId=${encodeURIComponent(sessionId!)}`, {
          cache: 'no-store',
        }).then((r) => r.json());
        // 'live' is only reported to testers (stored email on WALLET_LIVE_TEST_EMAILS).
        if (!cfg?.enabled || (cfg.mode !== 'mock' && cfg.mode !== 'live')) return;
        const m = await detectWallet(cfg.mode, cfg.methods ?? { apple_pay: false, google_pay: false });
        if (cancelled || !m) return;
        setAddressAutocomplete(!!cfg.addressAutocomplete);
        setMode(cfg.mode);
        setMethod(m);
        if (typeof cfg.email === 'string' && cfg.email) {
          setKnownEmail(cfg.email);
          setEmail(cfg.email);
        }
        // Restore an in-flight attempt after a reload so the page stays locked.
        if (bag) return; // bag attempts are tied to the cart, not a single run
        const act = await fetch(
          `/api/comix/wallet/active?runId=${encodeURIComponent(runId!)}&sessionId=${encodeURIComponent(sessionId!)}`,
          { cache: 'no-store' },
        ).then((r) => (r.ok ? r.json() : null));
        if (!cancelled && act?.attempt) {
          setAttempt(act.attempt);
          // Still waiting on the wallet → the grayed-out button says enough; no dialog. After a
          // reload the sheet is gone, so an 'authorizing' attempt is being confirmed server-side.
          if (act.attempt.phase !== 'authorizing') setOpen(true);
          else setChecking(true);
        }
      } catch {
        /* wallet stays hidden */
      } finally {
        if (!cancelled) setDetectDone(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ready, runId, sessionId]);

  // Switching Digital/Printed invalidates an un-started quote.
  useEffect(() => {
    setHf(null);
    setAttempt((a) => (a && a.phase === 'quoted' ? null : a));
    setErrors({});
    setUnavailable(false);
  }, [product, quantity, bag?.signature]);

  // Lock the rest of the page (Checkout etc.) only once money may have moved: a hold or
  // payment in progress, or a wallet result we're still confirming with Tranzila. While the
  // wallet sheet is simply open, Checkout stays usable — a cancel there is instant.
  const pageLocked = !!attempt?.locked && (attempt.phase !== 'authorizing' || checking);
  useEffect(() => {
    onLockedChange?.(pageLocked);
  }, [pageLocked, onLockedChange]);

  // Smart visibility: never show a button that can't open. Digital with a known email is
  // prepared in the background, so its button only appears once that succeeded.
  const preparing = product === 'digital' && !!knownEmail && !hf && !(attempt && attempt.phase !== 'quoted');
  const shown = !!(ready && method && mode && !unavailable && !preparing);
  useEffect(() => {
    onVisibleChange?.(shown);
  }, [shown, onVisibleChange]);
  // Still deciding whether the wallet button will appear (flag / device check, or a digital quote
  // being prepared in the background): the page shows a loading CTA of the same size meanwhile.
  // Never hold the page's CTA longer than RESOLVE_MAX_MS: after that Checkout shows, and the wallet
  // button still takes over if it becomes ready later.
  const [waitedTooLong, setWaitedTooLong] = useState(false);
  const resolvingRaw = ready && (!detectDone || (!!method && !!mode && !unavailable && preparing));
  useEffect(() => {
    if (!resolvingRaw || waitedTooLong) return;
    const t = setTimeout(() => setWaitedTooLong(true), RESOLVE_MAX_MS);
    return () => clearTimeout(t);
  }, [resolvingRaw, waitedTooLong]);
  const resolving = resolvingRaw && !waitedTooLong;
  useEffect(() => {
    onResolvingChange?.(resolving);
  }, [resolving, onResolvingChange]);

  // Meta: wallet authorized → AddPaymentInfo; paid internal order → Purchase (value charged).
  useEffect(() => {
    if (!attempt) return;
    if (['held', 'capturing', 'paid_order_pending', 'paid'].includes(attempt.phase)) walletMetaEvent('AddPaymentInfo', attempt);
    // Never double with Shopify: only for internal wallet orders (no Shopify order exists).
    if (attempt.phase === 'paid' && attempt.sitePurchaseEvent) walletMetaEvent('Purchase', attempt);
  }, [attempt?.phase, attempt?.attemptId, attempt?.sitePurchaseEvent]); // eslint-disable-line react-hooks/exhaustive-deps

  // Amplitude: how a wallet payment ended — once per attempt and outcome (also survives a reload
  // that restores the same attempt, via sessionStorage).
  useEffect(() => {
    if (!attempt) return;
    const outcome =
      attempt.phase === 'paid' ? 'paid'
      : attempt.phase === 'not_charged' ? attempt.declineReason ?? 'cancelled' // cancelled = sheet closed / expired
      : attempt.phase === 'released' ? 'released'
      : attempt.phase === 'support' ? 'needs_support'
      : null;
    if (!outcome) return;
    const key = `pwm_wallet_result:${attempt.attemptId}:${outcome}`;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    } catch { /* private mode: may repeat after a reload, never within the page */ }
    trackEvent('Wallet - Payment Result', {
      outcome, // paid | cancelled | declined | card_not_supported | released | needs_support
      method,
      product: attempt.product,
      quantity: attempt.quantity ?? 1,
      totalCents: attempt.totalCents,
      placement: bag ? 'bag' : 'unlock',
    });
  }, [attempt?.phase, attempt?.attemptId, attempt?.declineReason]); // eslint-disable-line react-hooks/exhaustive-deps

  // Paid (Tranzila capture verified + the paid-order pipeline ran) → same landing as the
  // Shopify checkout's return_to: the approve page, with the same "just purchased" markers.
  useEffect(() => {
    if (attempt?.phase !== 'paid') return;
    const runIds = attempt.runIds?.length ? attempt.runIds : runId ? [runId] : [];
    if (!runIds.length) return;
    try {
      for (const r of runIds) sessionStorage.setItem(`comix_checkout_pending_${r}`, String(Date.now()));
    } catch {
      /* private mode */
    }
    const anyPrinted = attempt.product === 'printed' || (attempt.items ?? []).some((i) => i.product === 'printed');
    markCheckoutStarted(); // pending-approval banner retries until the order lands (digital too)
    // Order summary screen (approve link per comic + "also in your email"). The summary is
    // handed over in sessionStorage — no session id or address ever goes into the URL.
    try {
      sessionStorage.setItem(
        'pwm_wallet_order_summary',
        JSON.stringify({
          orderName: attempt.orderName,
          totalCents: attempt.totalCents,
          runIds,
          email: knownEmail || email || null,
          method,
          items: attempt.items ?? [],
          shipTo: anyPrinted && addr.address1 ? `${addr.firstName} ${addr.lastName}, ${addr.address1}${addr.address2 ? `, ${addr.address2}` : ''}, ${addr.city}, ${addr.province} ${addr.zip}` : null,
          at: Date.now(),
        }),
      );
    } catch {
      /* private mode — the page falls back to the runIds in the URL */
    }
    // One book → straight to its approve page, exactly like the Shopify checkout's return_to.
    // Several (bag) → the summary page with a "Review & approve" link per book.
    const target = runIds.length === 1
      ? `/comix/approve?runId=${encodeURIComponent(runIds[0])}`
      : `/comix/order-complete?runIds=${runIds.map(encodeURIComponent).join(',')}`;
    let cancelled = false;
    (async () => {
      if (attempt.product === 'bag') {
        // Remove exactly the paid lines from the bag before leaving.
        await postJson('/api/comix/wallet/bag-clear', { attemptId: attempt.attemptId, sessionId }).catch(() => null);
      }
      await new Promise((r) => setTimeout(r, 1000));
      if (!cancelled) window.location.href = target;
    })();
    return () => {
      cancelled = true;
    };
  }, [attempt?.phase, attempt?.attemptId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cancelled / expired with nothing charged (no decline reason) → back to a fresh button,
  // silently. A decline or unsupported card still shows its message in the dialog, and any
  // real progress (held → paid, releasing…) opens the dialog so the customer sees it.
  useEffect(() => {
    if (!attempt) return;
    if (attempt.phase === 'not_charged' && !attempt.declineReason) {
      setAttempt(null);
      setHf(null);
      setChecking(false);
      return;
    }
    if (attempt.phase !== 'quoted' && attempt.phase !== 'authorizing') setOpen(true);
  }, [attempt?.phase, attempt?.attemptId]); // eslint-disable-line react-hooks/exhaustive-deps

  const poll = useCallback(
    (attemptId: string, tries = 0) => {
      if (pollRef.current) clearTimeout(pollRef.current);
      pollRef.current = setTimeout(async () => {
        const r = await fetch(
          `/api/comix/wallet/status?attemptId=${encodeURIComponent(attemptId)}&sessionId=${encodeURIComponent(sessionId!)}`,
          { cache: 'no-store', headers: adminHeader() },
        ).then((x) => (x.ok ? x.json() : null)).catch(() => null);
        if (r?.attempt) setAttempt(r.attempt);
        const phase: Phase | undefined = r?.attempt?.phase;
        // A failed/empty fetch (network blip, cold start) must still retry — never let one bad
        // tick silently strand the page locked with nothing left to un-stick it.
        const stillWaiting = r == null || (phase && IN_FLIGHT.includes(phase));
        // ~4 min: covers the server's 2-minute "ask Tranzila" reconciliation after a silent sheet.
        if (stillWaiting && tries < 90) poll(attemptId, tries + 1);
        if (phase && !IN_FLIGHT.includes(phase)) setChecking(false);
        else if (!stillWaiting) setChecking(false); // tries exhausted with no resolution — stop spinning forever
      }, tries < 10 ? 1000 : 3000); // fast at first: a closed sheet usually settles in seconds
    },
    [sessionId],
  );
  useEffect(() => () => { if (pollRef.current) clearTimeout(pollRef.current); }, []);
  useEffect(() => {
    if (attempt && IN_FLIGHT.includes(attempt.phase)) poll(attempt.attemptId);
  }, [attempt?.attemptId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Digital + known email: prepare in the background so tapping Apple Pay opens Apple's
  // sheet immediately (no form). Re-prepared shortly before the quote expires.
  useEffect(() => {
    if (!method || !mode || !knownEmail || product !== 'digital' || unavailable) return;
    if (attempt && attempt.phase !== 'quoted') return; // something is in flight / done
    const fresh = attempt && hf && new Date(attempt.quoteExpiresAt).getTime() - Date.now() > 60_000;
    if (fresh) {
      const t = setTimeout(() => setHf(null), new Date(attempt!.quoteExpiresAt).getTime() - Date.now() - 60_000);
      return () => clearTimeout(t);
    }
    void getQuote(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [method, mode, knownEmail, product, quantity, hf, attempt?.phase, bag?.signature, unavailable]);


  const contactInput = (confirmed = addrConfirmed, a: ShippingAddressInput = addr) => ({
    email,
    shippingAddress: product === 'printed' ? a : null,
    ...(product === 'printed' && confirmed ? { addressConfirmed: true } : {}),
  });

  // 2. Server quote → handshake (prepared, not yet authorizing). `silent` = background
  //    preparation for Digital (no form, no errors shown) so a tap can open the sheet directly.
  const getQuote = async (silent = false, confirmed = addrConfirmed, addrOverride?: ShippingAddressInput) => {
    const forProduct = product;
    const forQuantity = quantity;
    const stale = () => productRef.current !== forProduct || quantityRef.current !== forQuantity;
    const v = validateContact(product, contactInput(confirmed, addrOverride));
    if (!v.ok) {
      if (silent) setUnavailable(true); // background prepare can't succeed — show Checkout instead
      else setErrors(v.errors);
      return;
    }
    if (!silent) {
      setErrors({});
      setBusy(true);
    }
    // Anything the customer can't fix (rate limit, not available, can't start) → hide express
    // pay and let Checkout take over, instead of showing a message.
    const giveUp = () => {
      setOpen(false);
      setHf(null);
      setUnavailable(true);
    };
    try {
      const q = bag
        ? await postJson('/api/comix/wallet/bag-quote', { sessionId, contact: contactInput(confirmed, addrOverride) })
        : await postJson('/api/comix/wallet/quote', { runId, sessionId, product, quantity, contact: contactInput(confirmed, addrOverride) });
      if (!q.ok) {
        if (stale()) return;
        if (q.json?.attempt) setAttempt(q.json.attempt);
        if (silent) {
          // Background prepare failed (rate limit, unavailable…): without this the button stays
          // "preparing" and the page's loading CTA never turns into Checkout.
          if (!q.json?.attempt) setUnavailable(true);
          return;
        }
        if (q.json?.error === 'address_unverified') {
          // Lulu flagged the address — show its warnings + suggestion; the customer decides.
          setAddrCheck({ warnings: q.json.addressWarnings ?? [], suggested: q.json.suggestedAddress ?? null });
          return;
        }
        if (q.json?.fieldErrors) {
          setErrors(q.json.fieldErrors);
          return;
        }
        if (q.json?.error === 'payment_in_progress') return;
        giveUp();
        return;
      }
      if (stale()) return; // product switched while quoting — this quote is for the other one
      if (forProduct === 'printed') {
        try {
          localStorage.setItem(SAVED_ADDR_KEY, JSON.stringify(addrOverride ?? addr));
        } catch {
          /* storage unavailable */
        }
      }
      const p = await postJson('/api/comix/wallet/prepare', { attemptId: q.json.attempt.attemptId, sessionId, walletMethod: method });
      if (stale()) return;
      if (!p.ok) {
        giveUp();
        return;
      }
      setAttempt(p.json.attempt);
      setHf(p.json.hostedFields);
      // The address was just typed in the form, so there is no separate "is this correct" step.
      setConfirmDest(true);
    } catch {
      // Network error: hide express pay and let Checkout take over (never a stuck button).
      if (!stale()) giveUp();
    } finally {
      if (!silent) setBusy(false);
    }
  };

  // 3. The wallet tap. No await before chargeWallet — the sheet must open in the gesture.
  const pay = () => {
    if (!attempt || !hf || !confirmDest || busy || !method || !mode) return;
    if (attempt.product !== wantKind || (!bag && (attempt.quantity ?? 1) !== quantity)) {
      // Belt and braces: the quote on hand is for another product — discard, re-quote.
      setHf(null);
      setAttempt(null);
      setConfirmDest(false);
      return;
    }
    if (new Date(attempt.quoteExpiresAt) <= new Date()) {
      // Expired quote: refresh it quietly and show the new one — no message.
      setHf(null);
      setAttempt(null);
      void getQuote();
      return;
    }
    setBusy(true);
    void postJson('/api/comix/wallet/begin', { attemptId: attempt.attemptId, sessionId }, true);
    // Same funnel signals as the Shopify "Checkout" button: AddToCart (it skips the bag) +
    // InitiateCheckout (Shopify would send this on its checkout; here we must).
    if (!bag) walletMetaEvent('AddToCart', attempt); // bag lines already fired AddToCart when added
    walletMetaEvent('InitiateCheckout', attempt);
    trackEvent('Checkout - Proceed', { product: attempt.product, source: `wallet-${method}` });
    // No "waiting" dialog: the customer can't do anything while the wallet sheet is up (or
    // while we confirm with Tranzila) — the wallet button just grays out until it resolves.
    setAttempt({ ...attempt, phase: 'authorizing', locked: true });
    const report = async (res: WalletOutcome) => {
      if (res.outcome === 'no_response' || res.outcome === 'error') setChecking(true);
      try {
        const r = await postJson('/api/comix/wallet/authorize-result', {
          attemptId: attempt.attemptId,
          sessionId,
          outcome: res.outcome,
          transactionId: 'transactionId' in res ? res.transactionId : undefined,
          clientError: 'clientError' in res ? res.clientError : undefined,
        });
        if (r.json?.attempt) {
          setAttempt(r.json.attempt);
          if (IN_FLIGHT.includes(r.json.attempt.phase)) poll(attempt.attemptId);
          if (r.json.attempt.phase === 'not_charged') setHf(null);
          return;
        }
      } catch {
        /* fall through to the polling fallback below — never leave the page stuck locked */
      }
      // The report call itself failed (network blip, cold start) or came back without an
      // attempt — fall back to status polling, which independently asks the server to
      // reconcile with Tranzila. Without this, a failed fetch here used to strand the page
      // locked forever with no way to recover.
      poll(attempt.attemptId);
    };
    // A late callback (after the watchdog) is reported too — the server reconciles it.
    chargeWallet(method, mode, hf, (late) => void report(late))
      .then(report)
      .finally(() => setBusy(false));
  };

  // Fallback when the wallet sheet was closed but no signal arrived: the server checks
  // Tranzila right away — nothing reserved → cancelled; a hold that does exist is picked up.
  const cancelWaiting = async () => {
    if (!attempt) return;
    setChecking(true);
    try {
      const r = await postJson('/api/comix/wallet/authorize-result', {
        attemptId: attempt.attemptId,
        sessionId,
        outcome: 'cancelled_by_customer',
        clientError: 'customer tapped cancel while waiting for the wallet',
      });
      if (r.json?.attempt) {
        setAttempt(r.json.attempt);
        if (IN_FLIGHT.includes(r.json.attempt.phase)) poll(attempt.attemptId);
        if (r.json.attempt.phase === 'not_charged') setHf(null);
        return;
      }
    } catch {
      /* fall through — never leave the page stuck locked */
    }
    poll(attempt.attemptId);
  };

  const close = () => {
    // Never let the sheet vanish mid-payment; the page also stays locked via onLockedChange.
    if (attempt && IN_FLIGHT.includes(attempt.phase)) return;
    setOpen(false);
  };

  const field = (
    key: keyof ShippingAddressInput,
    label: string,
    autoComplete: string,
    extra: Partial<React.InputHTMLAttributes<HTMLInputElement>> = {},
  ) => (
    <label className="flex flex-col gap-[4px] text-[16px] text-[#333]">
      <span className="font-medium">{label}</span>
      <input
        name={key}
        autoComplete={autoComplete}
        value={(addr[key] as string) ?? ''}
        onChange={(e) => {
          editAddr({ ...addr, [key]: e.target.value });
          if (key === 'address1') setAddrQuery(e.target.value);
        }}

        aria-invalid={!!errors[key]}
        className={`h-[48px] rounded-[12px] border bg-white px-3 text-[16px] text-[#333] outline-none focus:border-[#F52F82] ${errors[key] ? 'border-red-500' : 'border-[#d2d2d2]'}`}
        {...extra}
      />
      {errors[key] && <span className="text-[16px] text-red-600">{errors[key]}</span>}
    </label>
  );

  // While the wallet is pending ('authorizing') the quote view stays as it was, with the
  // wallet button grayed out — no separate waiting screen.
  const pending = attempt?.phase === 'authorizing';
  const quoted = attempt && attempt.product === wantKind && (bag || (attempt.quantity ?? 1) === quantity) && (attempt.phase === 'quoted' || pending) && hf;
  const statusPhase = attempt && attempt.phase !== 'quoted' && !pending ? attempt.phase : null;
  // The printed address step is a full-height page; the order / status steps stay a bottom sheet.
  const fullScreen = product === 'printed' && !quoted && !statusPhase;
  // Phones: the address step fills the screen. Desktop: a centered box (max 400×600) whose
  // scrollbar is always visible.
  const pageMode = fullScreen && !isDesktop;
  const boxedAddress = fullScreen && isDesktop;
  const inFlight = !!(attempt && IN_FLIGHT.includes(attempt.phase));

  // Back: from the pay step of a printed order → the address form; otherwise close the sheet.
  const goBack = () => {
    if (quoted && product === 'printed' && !pending) {
      setHf(null);
      setAttempt(null);
      return;
    }
    close();
  };

  const backButton = !inFlight && (
    <button type="button" onClick={statusPhase ? close : goBack} aria-label="Back" className="-ml-2 flex h-9 w-9 items-center justify-center text-[#333]">
      <svg viewBox="0 0 20 20" fill="currentColor" className="h-6 w-6" aria-hidden="true">
        <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 0 1 0 1.06L9.06 10l3.73 3.71a.75.75 0 1 1-1.06 1.06l-4.25-4.24a.75.75 0 0 1 0-1.06l4.25-4.24a.75.75 0 0 1 1.06 0Z" clipRule="evenodd" />
      </svg>
    </button>
  );

  // Not shown: render nothing. (After every closure above is defined — the background prepare
  // effect calls getQuote while the button is still hidden.)
  if (!shown) return null;

  return (
    <>
      <WalletButton
        method={method}
        onClick={() => {
          const direct = product === 'digital' && attempt?.product === 'digital' && attempt?.phase === 'quoted' && !!hf && new Date(attempt.quoteExpiresAt) > new Date();
          // Every tap on the wallet button (before the address / order step). Fire-and-forget:
          // nothing may be awaited here, the wallet sheet must open inside the tap.
          trackEvent('Wallet - Button Tapped', {
            method,
            product: bag ? 'bag' : product,
            quantity: bag ? undefined : quantity,
            placement: bag ? 'bag' : 'unlock',
            opensSheet: direct, // prepared digital: straight to Apple/Google's sheet
          });
          // Prepared Digital quote → straight to the wallet sheet (must stay synchronous).
          if (direct) {
            pay();
            return;
          }
          setOpen(true);
          if (product === 'digital' && knownEmail && !hf) void getQuote();
        }}
        disabled={pending || busy}
        id="btn-wallet-express"
        className={buttonClassName}
      />

      <Dialog open={open} onClose={close} className="relative z-[60]">
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        <div className={`fixed inset-0 flex items-end justify-center ${pageMode ? '' : 'md:items-center md:p-4'}`}>
          <Dialog.Panel className={`w-full ${pageMode ? '' : boxedAddress ? 'md:max-w-[400px]' : 'md:max-w-[440px]'}`}>
          <motion.div
            ref={panelRef}
            onScroll={fullScreen ? (e: React.UIEvent<HTMLDivElement>) => setScrolled(e.currentTarget.scrollTop > 0) : undefined}
            initial={false}
            animate={{
              height: pageMode
                ? viewH || '100dvh'
                : boxedAddress
                  ? Math.min(600, (viewH || 800) - 32)
                  : contentH ? Math.min(contentH, (viewH || 800) * 0.92) : 'auto',
              borderTopLeftRadius: pageMode ? 0 : 24,
              borderTopRightRadius: pageMode ? 0 : 24,
            }}
            transition={{ type: 'spring', stiffness: 380, damping: 36 }}
            className={`w-full bg-white text-[#333] ${boxedAddress ? 'overflow-y-scroll [&::-webkit-scrollbar]:w-[8px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[#c8c8c8]' : 'overflow-y-auto'} ${pageMode ? '' : 'md:rounded-[24px]'}`}
          >
           <div ref={innerRef} className={fullScreen ? '' : 'px-[20px] pb-[24px] pt-[20px]'}>
            <motion.div key={statusPhase ? 'status' : fullScreen ? 'address' : 'order'} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.22, delay: 0.06 }}>
            {statusPhase ? (
              <>
                {backButton && <div className="mb-1 flex">{backButton}</div>}
                <StatusPanel
                  phase={statusPhase}
                  attempt={attempt!}
                  runId={runId!}
                  checking={checking}
                  onCancel={cancelWaiting}
                  onRetry={() => { setAttempt(null); setHf(null); setChecking(false); }}
                />
              </>
            ) : (
              <>
                {fullScreen ? (
                  // Full-height address page: Close on the left, Checkout (small primary button) on the right.
                  // No divider until the form scrolls under the header.
                  <div className={`sticky top-0 z-10 flex items-center justify-between border-b bg-white px-[16px] pb-[12px] pt-[max(12px,env(safe-area-inset-top))] transition-colors ${scrolled ? 'border-[#ececec]' : 'border-transparent'}`}>
                    <button type="button" onClick={close} aria-label="Close" className="-ml-[8px] flex h-[40px] w-[40px] items-center justify-center text-[#333]">
                      <svg className="h-[24px] w-[24px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                    <Dialog.Title className="sr-only">Delivery address (US only)</Dialog.Title>
                    <button
                      type="submit"
                      form="wallet-address-form"
                      disabled={busy}
                      className="h-[40px] rounded-[14px] px-[20px] font-primary text-[16px] font-semibold text-white shadow-[0_3px_2px_rgba(250,77,117,0.15)] disabled:opacity-60"
                      style={{ background: 'linear-gradient(to right, #F52F81 0%, #FD7468 100%)' }}
                    >
                      {busy ? 'One moment…' : 'Checkout'}
                    </button>
                  </div>
                ) : (
                  <div className="mb-4 flex items-center gap-3">
                    {backButton}
                    <Dialog.Title className="text-[20px] font-bold">
                      {quoted ? 'Your order' : product === 'printed' ? 'Delivery address (US only)' : 'Your email'}
                    </Dialog.Title>
                  </div>
                )}
                <form
                  id="wallet-address-form"
                  noValidate
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!quoted) void getQuote();
                  }}
                  className={`flex flex-col gap-[12px] ${fullScreen ? 'px-[20px] pb-[40px] pt-[20px]' : ''}`}
                >
                  {fullScreen && <h2 className="text-[22px] font-bold">Delivery address <span className="text-[16px] font-medium">(US only)</span></h2>}
                  {!quoted && !knownEmail && (
                    <label className="flex flex-col gap-[4px] text-[16px]">
                      <span className="font-medium">Email</span>
                      <input
                        type="email"
                        name="email"
                        autoComplete="email"
                        inputMode="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        aria-invalid={!!errors.email}
                        className={`h-[48px] rounded-[12px] border bg-white px-3 text-[16px] text-[#333] outline-none focus:border-[#F52F82] ${errors.email ? 'border-red-500' : 'border-[#d2d2d2]'}`}
                      />
                      {errors.email && <span className="text-[16px] text-red-600">{errors.email}</span>}
                      {product === 'digital' && <span className="text-[16px] text-[#555]">We’ll send your digital comic here.</span>}
                    </label>
                  )}

                  {product === 'printed' && !quoted && (
                    <fieldset className="flex flex-col gap-[12px]">
                      <legend className="sr-only">Delivery address (US only)</legend>
                      <div className="grid grid-cols-2 gap-[12px]">
                        {field('firstName', 'First name', 'shipping given-name', { maxLength: 34 })}
                        {field('lastName', 'Last name', 'shipping family-name', { maxLength: 34 })}
                      </div>
                      <div className="relative">
                        {field('address1', 'Street address', 'shipping address-line1', { maxLength: LULU_ADDRESS_LIMITS.street })}
                        {addrSuggestions.length > 0 && (
                          // In the flow (not absolute) so the sheet's scroll area can never clip it.
                          <ul role="listbox" data-addr-suggest className="mt-1 overflow-hidden rounded-xl border border-[#e0e0e0] bg-white shadow-lg">
                            {addrSuggestions.map((sug) => (
                              <li key={sug.placeId}>
                                <button
                                  type="button"
                                  className="w-full px-3 py-2.5 text-left text-[16px] hover:bg-[#f6f6f6]"
                                  // pointerdown beats the input's blur, which would hide the list first
                                  onPointerDown={async (e) => {
                                    e.preventDefault();
                                    setAddrSuggestions([]);
                                    setAddrQuery('');
                                    const r = await postJson('/api/comix/wallet/address', { sessionId, sessionToken: placesTokenRef.current, placeId: sug.placeId }).catch(() => null);
                                    placesTokenRef.current = null; // the details call ends this billing session
                                    const picked = r?.ok ? r.json?.address : null;
                                    if (picked) editAddr({ ...addr, ...picked, address2: picked.address2 ?? addr.address2 });
                                  }}
                                >
                                  {sug.text}
                                </button>
                              </li>
                            ))}
                            <li className="px-3 py-1 text-right text-[16px] text-[#999]">powered by Google</li>
                          </ul>
                        )}
                      </div>
                      {field('address2', 'Apt, suite (optional)', 'shipping address-line2', { maxLength: LULU_ADDRESS_LIMITS.street })}
                      {/* ZIP first — typing it fills City and State (see the ZIP lookup effect). */}
                      <div className="grid grid-cols-2 gap-[12px]">
                        {field('zip', 'ZIP code', 'shipping postal-code', { inputMode: 'numeric', maxLength: 10 })}
                        <label className="flex flex-col gap-[4px] text-[16px]">
                          <span className="font-medium">State</span>
                          <select
                            name="province"
                            autoComplete="shipping address-level1"
                            value={addr.province}
                            onChange={(e) => editAddr({ ...addr, province: e.target.value })}
                            aria-invalid={!!errors.province}
                            className={`h-[48px] rounded-[12px] border bg-white px-3 text-[16px] text-[#333] ${errors.province ? 'border-red-500' : 'border-[#d2d2d2]'}`}
                          >
                            <option value="">Select</option>
                            {US_STATES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
                          </select>
                          {errors.province && <span className="text-[16px] text-red-600">{errors.province}</span>}
                        </label>
                      </div>
                      {field('city', 'City', 'shipping address-level2', { maxLength: LULU_ADDRESS_LIMITS.city })}
                      {/* Country is always US — said once in the header ("US only"), no field for it. */}
                      <input type="hidden" name="countryCode" autoComplete="shipping country" value="US" />
                      {field('phone', 'Phone (for the courier)', 'shipping tel', {
                        type: 'tel',
                        inputMode: 'tel',
                        maxLength: LULU_ADDRESS_LIMITS.phone,
                        placeholder: '(212) 555-1234',
                        // Show a valid US number back as (212) 555-1234 once the customer leaves the field.
                        onBlur: () => {
                          const f = formatUsPhone(addr.phone);
                          if (f !== addr.phone) editAddr({ ...addr, phone: f });
                        },
                      })}
                    </fieldset>
                  )}


                  {addrCheck && product === 'printed' && !quoted && (
                    <div role="alert" className="rounded-2xl border border-amber-300 bg-amber-50 p-4 text-[16px] text-[#5c4300]">
                      <p className="font-semibold">Please double-check your address</p>
                      {addrCheck.warnings.length > 0 && (
                        <ul className="mt-1 list-disc pl-5">
                          {addrCheck.warnings.map((w) => <li key={w}>{w}</li>)}
                        </ul>
                      )}
                      {addrCheck.suggested && (
                        <p className="mt-2">
                          Suggested: <strong>{[addrCheck.suggested.address1 ?? addr.address1, addrCheck.suggested.address2 ?? addr.address2, addrCheck.suggested.city ?? addr.city, `${addrCheck.suggested.province ?? addr.province} ${addrCheck.suggested.zip ?? addr.zip}`].filter(Boolean).join(', ')}</strong>
                        </p>
                      )}
                      <div className="mt-3 flex flex-col gap-2">
                        {addrCheck.suggested && (
                          <button
                            type="button"
                            id="btn-wallet-use-suggested-address"
                            className="h-11 rounded-xl bg-[#333] font-semibold text-white"
                            onClick={() => {
                              const next = { ...addr, ...(addrCheck.suggested as Partial<ShippingAddressInput>) };
                              editAddr(next);
                              void getQuote(false, false, next);
                            }}
                          >
                            Use suggested address
                          </button>
                        )}
                        <button
                          type="button"
                          id="btn-wallet-keep-address"
                          className="h-11 rounded-xl border border-[#c9a227] font-semibold"
                          onClick={() => {
                            setAddrConfirmed(true);
                            setAddrCheck(null);
                            void getQuote(false, true);
                          }}
                        >
                          Use my address anyway
                        </button>
                      </div>
                    </div>
                  )}


                  {quoted && attempt && (
                    <div className="flex flex-col gap-[6px] text-[18px] font-normal leading-[1.3]">
                      <Row label={`${productLabel}${(attempt.quantity ?? 1) > 1 ? ` × ${attempt.quantity}` : ''}`} value={formatUsdCents(attempt.subtotalCents)} was={!bag && attempt.listSubtotalCents && attempt.listSubtotalCents > attempt.subtotalCents ? formatUsdCents(attempt.listSubtotalCents) : undefined} />
                      {product === 'printed' && (
                        <Row label="Shipping" value={attempt.shippingCents > 0 ? formatUsdCents(attempt.shippingCents) : 'Free'} />
                      )}
                    </div>
                  )}

                  {quoted ? (
                    <>
                      <WalletButton method={method} onClick={pay} disabled={!confirmDest || busy || pending} id="btn-wallet-pay" className="mt-1" />
                      <TrustBadges product={attempt && attempt.product !== 'bag' ? attempt.product : bag?.hasPrinted === false ? 'digital' : 'printed'} />
                    </>
                  ) : fullScreen ? null : (
                    <button type="submit" disabled={busy} className="mt-1 h-[52px] w-full rounded-2xl text-[18px] font-semibold text-white disabled:opacity-60" style={{ background: 'linear-gradient(90deg, #F52F82, #fd7468)' }}>
                      {busy ? 'One moment…' : 'Continue'}
                    </button>
                  )}
                </form>
              </>
            )}
            </motion.div>
           </div>
          </motion.div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </>
  );
}

// Heroicons (outline, 24px, MIT) inlined so there is no new dependency.
const HERO_PATHS = {
  bolt: 'm3.75 13.5 10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z',
  lock: 'M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z',
  heart: 'M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z',
} as const;

// Two short reassurance lines under the wallet button on the "Your order" step.
function TrustBadges({ product }: { product: 'printed' | 'digital' }) {
  const items = [
    product === 'digital'
      ? { icon: HERO_PATHS.bolt, text: 'Arrives instantly' }
      : { icon: HERO_PATHS.heart, text: 'Loved by thousands' },
    { icon: HERO_PATHS.lock, text: 'Secure payment' },
  ];
  return (
    <ul className="mt-[16px] flex flex-wrap items-center justify-center gap-x-3 gap-y-1 font-primary text-[16px] font-normal text-[#555]">
      {items.map((it) => (
        <li key={it.text} className="flex items-center gap-1 whitespace-nowrap">
          <svg className="h-[16px] w-[16px] shrink-0" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" aria-hidden="true">
            <path d={it.icon} />
          </svg>
          {it.text}
        </li>
      ))}
    </ul>
  );
}

function Row({ label, value, was }: { label: string; value: string; was?: string }) {
  return (
    <div className="flex justify-between">
      <span>{label}</span>
      <span>{was && <span className="mr-2 font-normal text-[#828282] line-through">{was}</span>}{value}</span>
    </div>
  );
}

/** Simple stand-ins for the card brands we don't accept. Swap in official artwork if desired. */
function UnsupportedCardLogos() {
  return (
    <div className="flex gap-3" aria-hidden="true">
      <span className="flex h-11 w-16 items-center justify-center rounded-lg bg-[#2e77bc] text-[16px] font-extrabold italic tracking-wide text-white">AMEX</span>
      <span className="flex h-11 w-16 items-center justify-center rounded-lg border border-[#d2d2d2] bg-white">
        <svg width="34" height="34" viewBox="0 0 34 34">
          <circle cx="17" cy="17" r="15" fill="#0079be" />
          <circle cx="17" cy="17" r="11" fill="#fff" />
          <path d="M13 9v16M21 9v16" stroke="#0079be" strokeWidth="3" />
        </svg>
      </span>
    </div>
  );
}

function StatusPanel({
  phase,
  attempt,
  runId,
  checking,
  onCancel,
  onRetry,
}: {
  phase: Phase;
  attempt: AttemptView;
  runId: string;
  checking?: boolean;
  onCancel: () => void;
  onRetry: () => void;
}) {
  // Offer "Cancel" while waiting on the wallet (not while confirming) after a few seconds.
  const [showCancel, setShowCancel] = useState(false);
  useEffect(() => {
    setShowCancel(false);
    if (phase !== 'authorizing' || checking) return;
    const t = setTimeout(() => setShowCancel(true), 4000);
    return () => clearTimeout(t);
  }, [phase, checking]);
  // Headline only while things are working; failures add one short line about the money.
  const copy: Record<Exclude<Phase, 'quoted'>, { title: string; body?: string; spin?: boolean }> = {
    authorizing: { title: checking ? 'Checking your payment…' : 'Confirm in your wallet', spin: true },
    held: { title: 'Finishing your payment…', spin: true },
    capturing: { title: 'Finishing your payment…', spin: true },
    paid_order_pending: { title: 'Creating your order…', spin: true },
    paid: { title: 'Payment complete', spin: true },
    releasing: { title: 'Payment cancelled', body: 'You weren’t charged.' },
    released: { title: 'Payment cancelled', body: 'You weren’t charged.' },
    not_charged:
      attempt.declineReason === 'card_not_supported'
        ? { title: 'We don’t accept these cards', body: 'Please use a different card.' }
        : attempt.declineReason === 'declined'
          ? { title: 'Payment declined', body: 'You weren’t charged.' }
          : { title: 'Payment didn’t go through', body: 'You weren’t charged.' },
    support: { title: 'We’re checking your payment', body: 'Don’t pay again. We’ll email you.' },
  };
  const c = copy[phase as Exclude<Phase, 'quoted'>];
  const unsupportedCard = phase === 'not_charged' && attempt.declineReason === 'card_not_supported';
  return (
    <div className="flex flex-col items-center gap-3 py-4 text-center" role="status" aria-live="polite">
      {c.spin && <span className="h-8 w-8 animate-spin rounded-full border-4 border-[#F52F82]/30 border-t-[#F52F82]" aria-hidden="true" />}
      <h3 className="text-[20px] font-bold leading-snug">{c.title}</h3>
      {c.body && <p className="text-[16px] leading-snug text-[#555]">{c.body}</p>}
      {unsupportedCard && <UnsupportedCardLogos />}
      {phase === 'paid' && (
        <a href={`/comix/approve?runId=${encodeURIComponent(runId)}`} className="mt-2 flex h-[52px] w-full items-center justify-center rounded-2xl text-[17px] font-semibold text-white" style={{ background: 'linear-gradient(90deg, #F52F82, #fd7468)' }}>
          Review my book
        </a>
      )}
      {phase === 'authorizing' && !checking && showCancel && (
        <button type="button" onClick={onCancel} className="mt-1 h-12 w-full rounded-2xl border border-[#d2d2d2] text-[16px] font-semibold text-[#555]">
          Cancel
        </button>
      )}
      {phase === 'not_charged' && (
        <button type="button" onClick={onRetry} className="mt-2 h-[52px] w-full rounded-2xl border-2 border-[#F52F82] text-[17px] font-semibold text-[#F52F82]">
          Try again
        </button>
      )}
    </div>
  );
}

/**
 * Wallet buttons. Apple Pay uses WebKit's native `-apple-pay-button` rendering (Apple's
 * official button). Google Pay follows Google's brand guidelines (black, "Buy with" + G Pay
 * mark). Swap in Google's downloadable button asset before launch if brand review asks.
 */
function WalletButton({ method, onClick, disabled, id, className = '' }: { method: WalletMethod; onClick: () => void; disabled?: boolean; id: string; className?: string }) {
  if (method === 'apple_pay') {
    return (
      <button
        id={id}
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label="Buy with Apple Pay"
        lang="en"
        className={`h-[52px] w-full rounded-2xl disabled:opacity-50 ${className}`}
        style={
          {
            // WebKit-only: renders Apple's official button (-apple-pay-button-type/style).
            WebkitAppearance: '-apple-pay-button',
            ApplePayButtonType: 'buy',
            ApplePayButtonStyle: 'black',
            backgroundColor: '#000',
          } as React.CSSProperties
        }
      />
    );
  }
  return (
    <button
      id={id}
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label="Buy with Google Pay"
      className={`flex h-[52px] w-full items-center justify-center gap-2 rounded-2xl bg-black text-[17px] font-medium text-white disabled:opacity-50 ${className}`}
    >
      <span>Buy with</span>
      <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#4285F4" d="M45.1 24.5c0-1.6-.1-3.1-.4-4.5H24v8.5h11.8c-.5 2.7-2.1 5-4.4 6.6v5.5h7.1c4.2-3.8 6.6-9.5 6.6-16.1z" />
        <path fill="#34A853" d="M24 46c5.9 0 10.9-2 14.5-5.4l-7.1-5.5c-2 1.3-4.5 2.1-7.4 2.1-5.7 0-10.5-3.8-12.2-9H4.5v5.7C8.1 41.1 15.5 46 24 46z" />
        <path fill="#FBBC05" d="M11.8 28.2c-.4-1.3-.7-2.7-.7-4.2s.3-2.9.7-4.2v-5.7H4.5C3 17.1 2 20.4 2 24s1 6.9 2.5 9.9l7.3-5.7z" />
        <path fill="#EA4335" d="M24 10.8c3.2 0 6.1 1.1 8.4 3.3l6.3-6.3C34.9 4.2 29.9 2 24 2 15.5 2 8.1 6.9 4.5 14.1l7.3 5.7c1.7-5.2 6.5-9 12.2-9z" />
      </svg>
      <span>Pay</span>
    </button>
  );
}
